import React from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import { useAccountQuery } from "../../../features/auth/authApi";

interface FormValues {
  sow: string;
  sowOther: string;
  tradedAssets: string[];
  otherTradingExperience: string;
  tradeFrequency: string;
  expectedCryptoVolume: string;
  sowConfirmation: boolean;
}

const WealthAndTradingQuestionnaire = () => {
  const navigate = useNavigate();
  const { data: account } = useAccountQuery();

  const initialValues: FormValues = {
    sow: "",
    sowOther: "",
    tradedAssets: [],
    otherTradingExperience: "",
    tradeFrequency: "",
    expectedCryptoVolume: "",
    sowConfirmation: false,
  };

  const saved = account?.user.form2_json;
  let parsed = {};
  if (saved) {
    try {
      parsed = typeof saved === "object" ? saved : JSON.parse(saved);
    } catch {
      console.warn("Cannot parse form2_json");
    }
  }

  const formik = useFormik<FormValues>({
    initialValues: { ...initialValues, ...parsed },
    validationSchema: Yup.object({
      sow: Yup.string().required("Please select your Source of Wealth"),
      sowOther: Yup.string().when("sow", {
        is: (val: string) => val?.startsWith("Other Sources"),
        then: Yup.string().required("Please specify other source"),
        otherwise: Yup.string(),
      }),
      sowConfirmation: Yup.boolean()
        .oneOf([true], "You must confirm the source of wealth")
        .required("You must confirm the source of wealth"),
      tradedAssets: Yup.array().min(
        1,
        "Please select at least one trading asset"
      ),
      tradeFrequency: Yup.string().required("Please select trade frequency"),
      expectedCryptoVolume: Yup.string().required(
        "Please select expected crypto volume"
      ),
    }),
    onSubmit: (values) => {
      localStorage.setItem("form2_json", JSON.stringify(values));
      navigate("/separate-form-upload-2");
    },
  });

  const tradedAssets = [
    "Digital Assets",
    "Futures",
    "FX",
    "Equities",
    "Options",
    "None of the above",
  ];
  const tradeFrequencies = [
    "Less than 1 trade per month",
    "1-5 Trades per month",
    "5-10 Trades per month",
    "10-30 Trades per month",
    "30+ Trades per month",
  ];
  const expectedVolumes = [
    "Under € 1,000",
    "From € 1,000 to € 10,000",
    "From € 10,000 to € 100,000",
    "From € 100,000",
  ];

  return (
    <Box mt={4} display="flex" justifyContent="center">
      <Container maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <Card elevation={10}>
            <CardContent>
              <Stack spacing={4}>
                <Typography variant="h6">
                  Please indicate your Source of Wealth (SOW)
                </Typography>
                <FormControl
                  fullWidth
                  error={formik.touched.sow && Boolean(formik.errors.sow)}
                >
                  <FormLabel>
                    *Please choose the right option and find examples of
                    required documents to support your SOW
                  </FormLabel>
                  <RadioGroup
                    name="sow"
                    value={formik.values.sow}
                    onChange={formik.handleChange}
                    onBlur={() => formik.setFieldTouched("sow", true)}
                  >
                    {[
                      {
                        label: "Income from Employment",
                        description:
                          "Payslips for the past 3 months & bank statement showing the salary being paid into this bank account; Personal tax statement",
                      },
                      {
                        label: "Income from/ownership of business activity",
                        description:
                          "Dividend contract note or equivalent & a bank statement showing these funds being deposited into the account; Copy of latest audited financial statements; Tax declaration form",
                      },
                      {
                        label: "Sale of Investment or Property",
                        description:
                          "Sale agreement, Proof of receipt of the settled sale amount, Proof of the transfer of the ownership of the asset, Bank Statement showing receipt of the settled sale proceeds",
                      },
                      {
                        label: "Inheritance",
                        description:
                          "Confirmation from Attorney detailing inheritance and its value",
                      },
                      {
                        label:
                          "Compensation from Judiciary, claims, or other settlement",
                        description:
                          "Confirmation from Attorney detailing the settlement, Confirmation of insurance claim or other claim payout",
                      },
                      {
                        label: "Other Sources (please specify)",
                        description: "",
                      },
                    ].map((option) => (
                      <Box key={option.label} sx={{ mb: 2 }}>
                        <FormControlLabel
                          value={option.label}
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body1" fontWeight="bold">
                                {option.label}
                              </Typography>
                              {option.description && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {option.description}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </Box>
                    ))}
                  </RadioGroup>
                  {formik.touched.sow && formik.errors.sow && (
                    <Typography variant="body2" color="error">
                      {formik.errors.sow}
                    </Typography>
                  )}
                </FormControl>
                {formik.values.sow.startsWith("Other Sources") && (
                  <TextField
                    label="Please specify"
                    name="sowOther"
                    fullWidth
                    value={formik.values.sowOther}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.sowOther && Boolean(formik.errors.sowOther)
                    }
                    helperText={
                      formik.touched.sowOther && formik.errors.sowOther
                    }
                  />
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      name="sowConfirmation"
                      checked={formik.values.sowConfirmation}
                      onChange={formik.handleChange}
                    />
                  }
                  label="I/we further confirm that these funds are derived from legitimate sources as stated above and that I/we will also provide the required evidence of the source of wealth if required to do so in future."
                />
                {formik.touched.sowConfirmation &&
                  formik.errors.sowConfirmation && (
                    <Typography color="error" variant="body2" sx={{ mt: -1 }}>
                      {formik.errors.sowConfirmation}
                    </Typography>
                  )}
                <Typography variant="h6">Trading Experience</Typography>
                <FormControl component="fieldset">
                  <FormLabel>
                    Which of the following have you traded in the past 12
                    months?
                  </FormLabel>
                  <Box>
                    {tradedAssets.map((asset) => (
                      <FormControlLabel
                        key={asset}
                        control={
                          <Checkbox
                            checked={formik.values.tradedAssets.includes(asset)}
                            disabled={
                              formik.values.tradedAssets.includes(
                                "None of the above"
                              ) && asset !== "None of the above"
                            }
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const name = e.target.name;

                              if (name === "None of the above") {
                                if (checked) {
                                  formik.setFieldValue("tradedAssets", [
                                    "None of the above",
                                  ]);
                                } else {
                                  formik.setFieldValue("tradedAssets", []);
                                }
                              } else {
                                let updated = [...formik.values.tradedAssets];
                                updated = updated.filter(
                                  (a) => a !== "None of the above"
                                );

                                if (checked) {
                                  updated.push(name);
                                } else {
                                  updated = updated.filter((a) => a !== name);
                                }

                                formik.setFieldValue("tradedAssets", updated);
                              }
                            }}
                            name={asset}
                          />
                        }
                        label={asset}
                      />
                    ))}
                    {formik.touched.tradedAssets &&
                      formik.errors.tradedAssets && (
                        <Typography color="error" variant="body2">
                          {formik.errors.tradedAssets}
                        </Typography>
                      )}
                  </Box>
                </FormControl>
                <TextField
                  label="Please indicate any other relevant trading experience if applicable:"
                  name="otherTradingExperience"
                  fullWidth
                  value={formik.values.otherTradingExperience}
                  onChange={formik.handleChange}
                />
                <FormControl
                  fullWidth
                  error={
                    formik.touched.tradeFrequency &&
                    Boolean(formik.errors.tradeFrequency)
                  }
                >
                  <FormLabel>Estimated Trade Frequency</FormLabel>
                  <RadioGroup
                    name="tradeFrequency"
                    value={formik.values.tradeFrequency}
                    onChange={formik.handleChange}
                    onBlur={() =>
                      formik.setFieldTouched("tradeFrequency", true)
                    }
                  >
                    {tradeFrequencies.map((freq) => (
                      <FormControlLabel
                        key={freq}
                        value={freq}
                        control={<Radio />}
                        label={freq}
                      />
                    ))}
                  </RadioGroup>
                  {formik.touched.tradeFrequency &&
                    formik.errors.tradeFrequency && (
                      <Typography variant="body2" color="error">
                        {formik.errors.tradeFrequency}
                      </Typography>
                    )}
                </FormControl>
                <FormControl
                  fullWidth
                  error={
                    formik.touched.expectedCryptoVolume &&
                    Boolean(formik.errors.expectedCryptoVolume)
                  }
                >
                  <FormLabel>
                    How much crypto do you expect to trade over the next 12
                    months?
                  </FormLabel>
                  <RadioGroup
                    name="expectedCryptoVolume"
                    value={formik.values.expectedCryptoVolume}
                    onChange={formik.handleChange}
                    onBlur={() =>
                      formik.setFieldTouched("expectedCryptoVolume", true)
                    }
                  >
                    {expectedVolumes.map((vol) => (
                      <FormControlLabel
                        key={vol}
                        value={vol}
                        control={<Radio />}
                        label={vol}
                      />
                    ))}
                  </RadioGroup>
                  {formik.touched.expectedCryptoVolume &&
                    formik.errors.expectedCryptoVolume && (
                      <Typography variant="body2" color="error">
                        {formik.errors.expectedCryptoVolume}
                      </Typography>
                    )}
                </FormControl>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  fullWidth
                  loading={formik.isSubmitting}
                >
                  Continue
                </LoadingButton>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};

export default WealthAndTradingQuestionnaire;
